import { Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/** United States zones, listed first (R22). */
export const US_TIME_ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Puerto_Rico",
  "Pacific/Guam",
  "Pacific/Pago_Pago",
] as const;

const US = new Set<string>(US_TIME_ZONES);

/** The browser's IANA zones, or none where `Intl.supportedValuesOf` is missing (older browsers, jsdom). */
const supportedTimeZones = (): string[] => {
  const supportedValuesOf = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  if (typeof supportedValuesOf !== "function") return [];
  try {
    return supportedValuesOf("timeZone");
  } catch {
    return [];
  }
};

/**
 * Options for the time zone field: US zones first, then every zone the browser knows, then the
 * current value when the browser doesn't list it (so a saved zone never disappears).
 */
export const timeZoneOptions = (current?: string | null): string[] => {
  const rest = supportedTimeZones().filter((zone) => !US.has(zone));
  const options = [...US_TIME_ZONES, ...rest];
  return current && !options.includes(current)
    ? [...options, current]
    : options;
};

/** "America/Los_Angeles" → "America/Los Angeles". */
export const timeZoneLabel = (zone: string) => zone.replace(/_/g, " ");

export interface TimeZoneFieldProps {
  /** The chosen IANA zone; null when none is set. */
  value: string | null;
  /** Called with the chosen zone, or null when the field is cleared. */
  onChange: (value: string | null) => void;
  label: string;
  /** A refusal for this field, such as `INVALID_TIMEZONE` copy. */
  error?: string | null;
  helperText?: string;
  disabled?: boolean;
  /** `data-testid` of the input; `district-timezone` by default. */
  testId?: string;
}

/**
 * Time zone picker (FR-041, R22): an Autocomplete over recognized IANA zones, clearable. The server
 * checks the zone again when saving (`INVALID_TIMEZONE`), since browser and server lists differ.
 */
export const TimeZoneField = ({
  value,
  onChange,
  label,
  error,
  helperText,
  disabled = false,
  testId = "district-timezone",
}: TimeZoneFieldProps) => {
  const { t } = useTranslation();
  const options = useMemo(() => timeZoneOptions(value), [value]);
  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={(_event, next) => onChange(next ?? null)}
      getOptionLabel={timeZoneLabel}
      groupBy={(zone) =>
        US.has(zone)
          ? t("district.timezoneGroupUs")
          : t("district.timezoneGroupAll")
      }
      disabled={disabled}
      autoHighlight
      clearOnEscape
      clearText={t("district.timezoneClear")}
      openText={t("district.timezoneOpen")}
      closeText={t("common.close")}
      noOptionsText={t("district.timezoneNoMatch")}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={Boolean(error)}
          helperText={error || helperText}
          inputProps={{ ...params.inputProps, "data-testid": testId }}
        />
      )}
    />
  );
};

export default TimeZoneField;
